import { Stack } from '@mui/material';
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useFieldArray } from 'react-hook-form';

import { AccordionContext } from '@/components/Accordion/Accordion.context';
import { SemanticTileKindNode } from '@/components/diagrams/components/Tile/Tile.utils';
import { useFormContext } from '@/components/react-hook-form';
import { defaultRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionTileIcon } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/DispositiveProvisionTileIcon';
import { PrimaryClientDeathSectionDistributionStatus } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionDistributionStatus';
import { PrimaryClientDeathSectionRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionRecipients';
import { PrimaryClientDeathSectionTemplateRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionTemplateRecipients';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import {
  useResiduaryPaysAllTaxes,
  useTemplateId,
  useToggleResiduaryPaysAllTaxes,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/formHooks';
import { PathsOf } from '@/types/subform';

import { PrimaryClientDeathSectionActions } from './PrimaryClientDeathSectionActions';
import { PrimaryClientDeathSectionLabel } from './PrimaryClientDeathSectionLabel';

interface PrimaryClientDeathSectionProps {
  deathEvent: PrimaryClientDeathEvent;
  node: SemanticTileKindNode;
  suggestionsMatcher?: ReactNode;
}

export function PrimaryClientDeathSection({
  deathEvent,
  node,
  suggestionsMatcher,
}: PrimaryClientDeathSectionProps) {
  const { control, setValue, setFocus } =
    useFormContext<DispositiveProvisionsFormV2Shape>();

  const recipientsFieldName =
    `${getDeathEventBasePath(deathEvent)}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const {
    append,
    remove,
    move,
    fields: recipients,
  } = useFieldArray({
    control,
    name: recipientsFieldName,
    keyName: '_id',
  });

  const { residuaryPaysAllTaxes } = useResiduaryPaysAllTaxes({ deathEvent });

  /**
   * Tracks the IDs of recipients that should be expanded by default.
   * When new recipients are added (single or bulk), their IDs are stored here
   * so the accordion knows to expand them on mount.
   */
  const [forceExpandIds, setForceExpandIds] = useState<string[]>([]);

  /**
   * Tracks the IDs of recipients that should be collapsed.
   * This is used to ensure that when a new recipient is added, all previous recipients are collapsed.
   */
  const [closedAccordionIds, setClosedAccordionIds] = useState<string[]>([]);

  /**
   * A ref to track if an addition was made to the recipients list.
   * This is used to determine if we should expand the most recently added recipient entry.
   */
  const hasUnhandledAddition = useRef(false);

  /**
   * Tracks how many recipients were added in the most recent addition,
   * so the useLayoutEffect can determine which recipients are new.
   */
  const pendingNewCount = useRef(0);

  const handleAddRecipient = useCallback(
    (prefilledValues?: Partial<DispositiveProvisionsFormRecipient>) => {
      const existingRecipientIds = recipients.map((r) => r._id);
      setClosedAccordionIds(existingRecipientIds);

      append(
        {
          ...defaultRecipient,
          bearsTax: !residuaryPaysAllTaxes,
          ...prefilledValues,
        },
        { shouldFocus: false }
      );
      pendingNewCount.current = 1;
      hasUnhandledAddition.current = true;
    },
    [append, recipients, residuaryPaysAllTaxes]
  );

  const handleBulkAddRecipients = useCallback(
    (newRecipients: DispositiveProvisionsFormRecipient[]) => {
      const existingRecipientIds = recipients.map((r) => r._id);
      setClosedAccordionIds(existingRecipientIds);

      const bearsTax = !residuaryPaysAllTaxes;

      const startIndex = recipients.length;
      append(newRecipients.map((r) => ({ ...r, bearsTax })));

      // Mark each new recipient's transferTaxKind as dirty so
      // useDefaultRecipientTransferTaxKind doesn't overwrite the values
      // chosen in the split modal.
      newRecipients.forEach((r, i) => {
        if (r.transferTaxKind) {
          setValue(
            `${recipientsFieldName}.${startIndex + i}.transferTaxKind` as PathsOf<DispositiveProvisionsFormV2Shape>,
            r.transferTaxKind as never,
            { shouldDirty: true }
          );
        }
      });

      pendingNewCount.current = newRecipients.length;
      hasUnhandledAddition.current = true;
    },
    [append, recipients, recipientsFieldName, residuaryPaysAllTaxes, setValue]
  );

  const handleRemoveRecipient = useCallback(
    (index: number) => {
      // Don't resetField before remove() — resetField clears the dirty flag at
      // this index, and after remove() shifts subsequent items down, the now-
      // shifted recipient inherits the cleared dirty state, causing
      // useDefaultRecipientTransferTaxKind to overwrite its transferTaxKind.
      remove(index);
    },
    [remove]
  );

  useLayoutEffect(() => {
    if (!hasUnhandledAddition.current) return;
    const count = pendingNewCount.current || 1;
    const newRecipients = recipients.slice(-count);
    if (newRecipients.length > 0) {
      // Scroll to the first new recipient so the user sees all additions
      const firstNew = newRecipients[0];
      if (firstNew) {
        const element = document.querySelector(
          `[data-rbd-draggable-id="${firstNew._id}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      setForceExpandIds(newRecipients.map((r) => r._id));

      // For a single addition, focus the recipient input so the user can pick a
      // recipient without clicking into it. The typeahead's menu stays closed
      // on focus (openOnFocus is disabled on the recipient field). Skip for bulk
      // adds, where focusing one of several new rows would be arbitrary.
      if (count === 1) {
        const recipientIdPath =
          `${recipientsFieldName}.${recipients.length - 1}.recipientId` as PathsOf<DispositiveProvisionsFormV2Shape>;
        // Defer so the accordion has expanded and the input is mounted.
        setTimeout(() => setFocus(recipientIdPath));
      }
    }
    pendingNewCount.current = 0;
    hasUnhandledAddition.current = false;
  }, [recipients, recipientsFieldName, setFocus]);

  // Watch for templateId to decide which recipients component to render
  const templateId = useTemplateId({ deathEvent });

  useToggleResiduaryPaysAllTaxes({ deathEvent });

  return (
    <Stack direction="column" spacing={2} mb={2}>
      <PrimaryClientDeathSectionLabel
        deathEvent={deathEvent}
        containerSx={{ mx: -3 }}
      />
      <Stack direction="row" justifyContent="space-between" spacing={1} px={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <DispositiveProvisionTileIcon
            size="large"
            variant="subtle"
            shape="circle"
            node={node}
          />
          <PrimaryClientDeathSectionDistributionStatus
            deathEvent={deathEvent}
            onAddRecipient={handleAddRecipient}
          />
        </Stack>
        <PrimaryClientDeathSectionActions
          deathEvent={deathEvent}
          onAddRecipient={handleAddRecipient}
          onBulkAddRecipients={handleBulkAddRecipients}
        />
      </Stack>
      {suggestionsMatcher}
      {(templateId || recipients.length > 0) && (
        <Stack direction="column" px={2}>
          {templateId ? (
            <PrimaryClientDeathSectionTemplateRecipients
              deathEvent={deathEvent}
              templateId={templateId}
            />
          ) : (
            <AccordionContext.Provider value={{ closedAccordionIds }}>
              <PrimaryClientDeathSectionRecipients
                deathEvent={deathEvent}
                fieldArrayProps={{
                  remove: handleRemoveRecipient,
                  move,
                  fields: recipients,
                }}
                forceExpandIds={forceExpandIds}
              />
            </AccordionContext.Provider>
          )}
        </Stack>
      )}
    </Stack>
  );
}
